import { t, useI18n } from '../../i18n';
import React, { useMemo, useState } from 'react';
import type { DutySlot, DutyColour, RosterRuleViolation } from '../../services/dutyBoardService';
import {
  formatAbsoluteMinute,
  formatDayOfWeek,
  formatDayNumber,
  type CoverageWindow,
} from '../../utils/siteScheduling';
import './WeekView.css';

const COLOUR_DOT: Record<DutyColour, string> = {
  sky: '#60a5fa',
  mint: '#34d399',
  amber: '#fbbf24',
  rose: '#f472b6',
  slate: '#94a3b8',
};

const COLOUR_BG: Record<DutyColour, string> = {
  sky: 'rgba(96,165,250,.14)',
  mint: 'rgba(52,211,153,.14)',
  amber: 'rgba(251,191,36,.14)',
  rose: 'rgba(244,114,182,.14)',
  slate: 'rgba(148,163,184,.14)',
};

interface DutyRowSummary {
  displayOrder: number;
  name: string;
  colour: DutyColour;
  dutyKind: string;
  startMinute: number;
  endMinute: number;
  assignedCount: number;
  requiredCount: number;
  blockCount: number;
}

function groupDutyRows(daySlots: DutySlot[]): DutyRowSummary[] {
  const grouped = new Map<number, DutySlot[]>();
  daySlots.forEach((s) => {
    const current = grouped.get(s.display_order);
    if (current) current.push(s);
    else grouped.set(s.display_order, [s]);
  });
  return Array.from(grouped.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([displayOrder, slots]) => {
      const sorted = [...slots].sort((a, b) => a.start_minute - b.start_minute);
      const primary = sorted[0];
      return {
        displayOrder,
        name: primary.name,
        colour: primary.colour,
        dutyKind: primary.duty_kind,
        startMinute: sorted[0].start_minute,
        endMinute: sorted[sorted.length - 1].end_minute,
        assignedCount: slots.reduce((n, s) => n + s.assignments.length, 0),
        requiredCount: slots.reduce((n, s) => n + s.guards_required, 0),
        blockCount: slots.length,
      };
    });
}

function formatTime(m: number, cw: CoverageWindow): string {
  return formatAbsoluteMinute(m, {
    showMidnightAs24: cw.startMinute === 0 && m === cw.endMinute && cw.endMinute === 1440,
  });
}

type DayStatus = 'published' | 'draft' | 'update' | 'empty';

function getDayStatus(daySlots: DutySlot[]): DayStatus {
  if (daySlots.length === 0) return 'empty';
  const hasPublished = daySlots.some((s) => s.is_published);
  const hasDraft = daySlots.some((s) => !s.is_published);
  if (hasPublished && hasDraft) return 'update';
  return hasPublished ? 'published' : 'draft';
}

const STATUS_LABEL: Record<DayStatus, string> = {
  published: 'Published - live',
  draft: 'Draft - not live',
  update: 'Publish changes',
  empty: 'No duties',
};

export interface WeekViewProps {
  weekDates: string[];
  weekSlots: Record<string, DutySlot[]>;
  rosterViolations: Record<string, RosterRuleViolation[]>;
  coverageWindow: CoverageWindow;
  copiedDate: string | null;
  /* Whether the copy on the clipboard carries its guard assignments. */
  copiedWithAssignments: boolean;
  todayKey: string;
  isBusy: boolean;
  onDayClick: (dateKey: string) => void;
  onCopyDay: (dateKey: string) => void;
  onPasteDay: (targetDates: string[]) => void;
  onClearDay: (dateKey: string) => void;
  onPublishDay: (dateKey: string) => void;
}

export default function WeekView({
  weekDates,
  weekSlots,
  rosterViolations,
  coverageWindow,
  copiedDate,
  copiedWithAssignments,
  todayKey,
  isBusy,
  onDayClick,
  onCopyDay,
  onPasteDay,
  onClearDay,
  onPublishDay,
}: WeekViewProps) {
  useI18n();
  const [confirmClear, setConfirmClear] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState<string | null>(null);

  const weekStats = useMemo(() => {
    let totalDuties = 0;
    let totalAssigned = 0;
    let totalGaps = 0;
    let totalWarnings = 0;
    let totalMinutes = 0;
    let publishedDays = 0;
    let draftDays = 0;
    let updateDays = 0;
    weekDates.forEach((dk) => {
      const daySlots = weekSlots[dk] ?? [];
      const dayWarnings = rosterViolations[dk] ?? [];
      const rows = groupDutyRows(daySlots);
      const status = getDayStatus(daySlots);
      if (status === 'published') publishedDays++;
      if (status === 'draft') draftDays++;
      if (status === 'update') updateDays++;
      totalDuties += rows.length;
      totalWarnings += dayWarnings.length;
      rows.forEach((r) => {
        totalAssigned += r.assignedCount;
        if (r.assignedCount < r.requiredCount) totalGaps++;
        totalMinutes += r.endMinute - r.startMinute;
      });
    });
    return {
      totalDuties,
      totalAssigned,
      totalGaps,
      totalWarnings,
      totalHours: (totalMinutes / 60).toFixed(1),
      publishedDays,
      draftDays,
      updateDays,
      pendingDays: draftDays + updateDays,
    };
  }, [rosterViolations, weekDates, weekSlots]);

  return (
    <div className="wv">
      <div className="wv__stats">
        <div className="wv__stat">
          <span className="wv__stat-label">{t('Weekly duties')}</span>
          <strong>{weekStats.totalDuties}</strong>
        </div>
        <div className="wv__stat">
          <span className="wv__stat-label">{t('Assigned')}</span>
          <strong>{weekStats.totalAssigned}</strong>
        </div>
        <div className="wv__stat">
          <span className="wv__stat-label">{t('Hours')}</span>
          <strong>{weekStats.totalHours}</strong>
        </div>
        <div className={`wv__stat${weekStats.totalGaps > 0 ? ' wv__stat--warning' : ''}`}>
          <span className="wv__stat-label">{t('Gaps')}</span>
          <strong>{weekStats.totalGaps}</strong>
        </div>
        <div className={`wv__stat${weekStats.pendingDays > 0 ? ' wv__stat--warning' : ''}`}>
          <span className="wv__stat-label">{t('Not live')}</span>
          <strong>{weekStats.pendingDays}</strong>
        </div>
        <div className={`wv__stat${weekStats.totalWarnings > 0 ? ' wv__stat--warning' : ''}`}>
          <span className="wv__stat-label">{t('Warnings')}</span>
          <strong>{weekStats.totalWarnings}</strong>
        </div>
      </div>

      {weekStats.totalDuties > 0 && (
        <div className={`wv__publish-reminder${weekStats.pendingDays > 0 ? ' wv__publish-reminder--warning' : ' wv__publish-reminder--published'}`} role="status">
          <strong>
            {weekStats.pendingDays > 0
              ? `${weekStats.pendingDays} day${weekStats.pendingDays === 1 ? '' : 's'} need publishing`
              : 'All planned days are published'}
          </strong>
          <span>
            {weekStats.pendingDays > 0
              ? 'Publish draft or changed days before guards can see those shifts.'
              : 'Published shifts are live and visible to assigned guards.'}
          </span>
        </div>
      )}

      <div className="wv__grid">
        {weekDates.map((dateKey) => {
          const daySlots = weekSlots[dateKey] ?? [];
          const dayWarnings = rosterViolations[dateKey] ?? [];
          const rows = groupDutyRows(daySlots);
          const status = getDayStatus(daySlots);
          const isToday = dateKey === todayKey;
          const isHistoric = dateKey < todayKey;
          const isCopied = dateKey === copiedDate;
          const hasClipboard = copiedDate !== null;
          const isPublishedLocked = (status === 'published' || status === 'update') && !isHistoric;
          const isConfirmingClear = confirmClear === dateKey;
          const isConfirmingPublish = confirmPublish === dateKey;
          const publishLabel = status === 'update' ? 'Update live roster' : 'Publish to live';

          return (
            <div
              key={dateKey}
              className={[
                'wv__day',
                isToday ? 'wv__day--today' : '',
                isCopied ? 'wv__day--copied' : '',
                `wv__day--${status}`,
                dayWarnings.length > 0 ? 'wv__day--rule-warning' : '',
              ].filter(Boolean).join(' ')}
            >
              <button
                className="wv__day-header"
                onClick={() => onDayClick(dateKey)}
                title={t('Open day view')}
              >
                <span className="wv__day-name">{formatDayOfWeek(dateKey)}</span>
                <span className={`wv__day-number${isToday ? ' wv__day-number--today' : ''}`}>
                  {formatDayNumber(dateKey)}
                </span>
              </button>

              <div className="wv__day-body">
                {rows.length === 0 && (
                  <div className="wv__day-empty">
                    {hasClipboard ? 'Paste to fill' : 'No duties'}
                  </div>
                )}
                {rows.map((row) => {
                  const isFull = row.assignedCount >= row.requiredCount;
                  return (
                    <div
                      key={row.displayOrder}
                      className="wv__duty-card"
                      style={{
                        borderLeftColor: COLOUR_DOT[row.colour],
                        background: COLOUR_BG[row.colour],
                      }}
                    >
                      <div className="wv__duty-name">{row.name}</div>
                      <div className="wv__duty-time">
                        {formatTime(row.startMinute, coverageWindow)} – {formatTime(row.endMinute, coverageWindow)}
                      </div>
                      <div className={`wv__duty-staff${isFull ? '' : ' wv__duty-staff--gap'}`}>
                        <span className="wv__duty-dots">
                          {Array.from({ length: Math.min(row.assignedCount, 4) }, (_, i) => (
                            <span
                              key={i}
                              className="wv__duty-dot"
                              style={{ background: COLOUR_DOT[row.colour] }}
                            />
                          ))}
                        </span>
                        {row.assignedCount}/{row.requiredCount}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="wv__day-footer">
                <span className={`wv__day-status wv__day-status--${status}`}>
                  {STATUS_LABEL[status]}
                </span>
                {dayWarnings.length > 0 && (
                  <span className="wv__day-warning" title={dayWarnings.map((violation) => violation.message).join('\n')}>
                    {dayWarnings.length} roster warning{dayWarnings.length === 1 ? '' : 's'}
                  </span>
                )}

                <div className="wv__day-actions">
                  {daySlots.length > 0 && (
                    <button
                      className={`wv__action-btn${isCopied ? ' wv__action-btn--active' : ''}`}
                      onClick={() => onCopyDay(dateKey)}
                      disabled={isBusy}
                      title={t('Copy this day\'s duties')}
                    >
                      {isCopied ? '✓ Copied' : 'Copy'}
                    </button>
                  )}

                  {hasClipboard && !isCopied && !isHistoric && !isPublishedLocked && (
                    <button
                      className="wv__action-btn wv__action-btn--paste"
                      onClick={() => onPasteDay([dateKey])}
                      disabled={isBusy}
                      title={copiedWithAssignments
                        ? t('Paste the copied duties and their guards here')
                        : t('Paste the copied duties here, unassigned')}
                    >
                      {copiedWithAssignments ? t('Paste + guards') : t('Paste')}
                    </button>
                  )}

                  {(status === 'draft' || status === 'update') && !isHistoric && (
                    <button
                      className="wv__action-btn wv__action-btn--publish"
                      onClick={() => {
                        if (isConfirmingPublish) {
                          onPublishDay(dateKey);
                          setConfirmPublish(null);
                          return;
                        }
                        setConfirmPublish(dateKey);
                      }}
                      disabled={isBusy}
                      title={status === 'update' ? 'Publish changes so guards see the latest shifts' : 'Publish this day so guards can see assigned shifts'}
                    >
                      {isConfirmingPublish ? 'Confirm publish' : publishLabel}
                    </button>
                  )}

                  {daySlots.length > 0 && !isHistoric && !isPublishedLocked && (
                    <>
                      {isConfirmingClear ? (
                        <span className="wv__clear-confirm">
                          <button
                            className="wv__action-btn wv__action-btn--danger"
                            onClick={() => { onClearDay(dateKey); setConfirmClear(null); setConfirmPublish(null); }}
                            disabled={isBusy}
                          >
                            {t('Confirm')}
                          </button>
                          <button
                            className="wv__action-btn"
                            onClick={() => setConfirmClear(null)}
                          >
                            {t('Cancel')}
                          </button>
                        </span>
                      ) : (
                        <button
                          className="wv__action-btn wv__action-btn--subtle"
                          onClick={() => setConfirmClear(dateKey)}
                          disabled={isBusy}
                          title={t('Clear all duties')}
                        >
                          {t('Clear')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
